<template>
  <div class="quota-insure-page">
    <Progress :active-index="progressStatus" />
    <a-spin :spinning="loading || submitting">
      <div
        v-if="errorMessage"
        class="error-block"
      >
        <a-alert
          type="error"
          show-icon
          :message="errorMessage"
        />
      </div>
      <div
        v-else-if="uiProfile"
        class="main"
      >
        <QuotaApplicationForm
          v-if="step === 0"
          ref="applicationForm"
          :order-info="orderInfo"
          :profile="uiProfile"
          :partners="partners"
          :channel-info="channelInfo"
          :last-account="lastAccount"
          :channel-code="channelCode"
          :order-no="orderNo"
        />
        <QuotaNoticeStep
          v-else-if="step === 1"
          ref="noticeStep"
          :notice="orderInfo.redTxt || channelInfo.redTxt"
          :files="orderInfo.files || channelInfo.readDocMap || []"
        />
        <QuotaOrderStatus
          v-else
          :order-info="orderInfo"
          :order-no="orderNo"
          :channel-code="channelCode"
          :submitting="submitting"
          :give-up-enabled="uiProfile.giveUpEnabled"
          @pay="openUrl"
          @open-url="openUrl"
          @giveup="confirmGiveUp"
          @surrender="submitSurrender"
          @invoice="submitInvoice"
          @refresh="loadInformation"
        />

        <div
          v-if="step < 2"
          class="operate"
        >
          <a-button
            v-if="step === 0 && uiProfile.giveUpEnabled && channelCode !== 'hami'"
            size="large"
            :disabled="submitting"
            @click="confirmGiveUp"
          >
            取消投保
          </a-button>
          <a-button
            v-if="step === 1"
            size="large"
            :disabled="submitting"
            @click="returnToModify"
          >
            返回修改信息
          </a-button>
          <a-button
            type="primary"
            size="large"
            :loading="submitting"
            @click="next"
          >
            {{ step === 0 ? '保存并下一步' : '确认投保' }}
          </a-button>
        </div>
      </div>
    </a-spin>
  </div>
</template>

<script>
import Progress from '@/components/Progress';
import {
  applyInvoice,
  confirmInsure,
  getChannelInfo,
  getInformation,
  getLastAccount,
  getPartners,
  getUiProfile,
  giveUp,
  saveInformation,
  surrender,
} from '@/api/quota';
import QuotaApplicationForm from './components/ApplicationForm';
import QuotaNoticeStep from './components/NoticeStep';
import QuotaOrderStatus from './components/OrderStatus';

const {
  getOrderStep,
  getProgressStatus,
  isOrderPollingGenerationActive,
  normalizeUiProfile,
  shouldPollOrderStatus,
} = require('./policy');

export default {
  name: 'InsureQuota',
  components: {
    Progress,
    QuotaApplicationForm,
    QuotaNoticeStep,
    QuotaOrderStatus,
  },
  data() {
    return {
      loading: false,
      submitting: false,
      errorMessage: '',
      uiProfile: null,
      orderInfo: {},
      channelInfo: {},
      partners: [],
      lastAccount: {},
      step: 0,
      pollTimer: null,
      pollGeneration: 0,
      destroyedFlag: false,
    };
  },
  computed: {
    channelCode() {
      return this.$route.query.channelCode || '';
    },
    orderNo() {
      return this.$route.query.orderNo || '';
    },
    progressStatus() {
      return getProgressStatus(this.orderInfo.status || '1');
    },
  },
  created() {
    this.initialize();
  },
  beforeDestroy() {
    this.destroyedFlag = true;
    this.pollGeneration += 1;
    this.stopPolling();
  },
  methods: {
    unwrap(response) {
      const body = response && response.data;
      if (body && body.data !== undefined) return body.data;
      return body || {};
    },
    errorText(error, fallback) {
      return error && error.response && error.response.data && error.response.data.message ||
        error && error.message || fallback;
    },
    ensureMutationSuccess(response) {
      const body = response && response.data || {};
      const status = body.status === undefined ? 200 : Number(body.status);
      if (status !== 200) throw new Error(body.message || '操作失败');
      return body.data || body;
    },
    async initialize() {
      if (!this.channelCode || !this.orderNo) {
        this.errorMessage = '缺少渠道编码或业务流水号';
        return;
      }
      this.loading = true;
      try {
        const profileResponse = await getUiProfile(this.channelCode, this.orderNo);
        this.uiProfile = normalizeUiProfile(this.unwrap(profileResponse));
        await this.loadInformation(false);
        if (this.step <= 1) await this.loadFormOptions();
      } catch (error) {
        this.errorMessage = this.errorText(error, '投保信息加载失败');
        this.$notification.error({ message: this.errorMessage });
      } finally {
        this.loading = false;
      }
    },
    async loadFormOptions() {
      const responses = await Promise.all([
        getChannelInfo(this.channelCode, this.orderNo),
        getPartners(this.channelCode, this.orderNo),
        getLastAccount(this.channelCode, this.orderNo),
      ]);
      this.channelInfo = this.unwrap(responses[0]) || {};
      const partnerData = this.unwrap(responses[1]);
      this.partners = Array.isArray(partnerData) ? partnerData : [];
      const accountData = this.unwrap(responses[2]);
      this.lastAccount = accountData && typeof accountData === 'object'
        ? accountData
        : { accountNo: accountData || '' };
    },
    async loadInformation(showLoading = true) {
      const generation = ++this.pollGeneration;
      if (showLoading) this.loading = true;
      this.stopPolling();
      try {
        const orderInfo = this.unwrap(await getInformation(this.channelCode, this.orderNo));
        if (!isOrderPollingGenerationActive(
          generation, this.pollGeneration, this.destroyedFlag
        )) return;
        this.orderInfo = orderInfo;
        this.step = getOrderStep(this.orderInfo.status);
        this.schedulePolling(generation);
      } catch (error) {
        if (isOrderPollingGenerationActive(
          generation, this.pollGeneration, this.destroyedFlag
        )) {
          this.$notification.error({ message: this.errorText(error, '订单状态查询失败') });
        }
      } finally {
        if (showLoading && isOrderPollingGenerationActive(
          generation, this.pollGeneration, this.destroyedFlag
        )) this.loading = false;
      }
    },
    schedulePolling(generation) {
      if (!isOrderPollingGenerationActive(
        generation, this.pollGeneration, this.destroyedFlag
      )) return;
      if (!shouldPollOrderStatus(this.orderInfo.status)) return;
      this.pollTimer = window.setTimeout(() => {
        if (isOrderPollingGenerationActive(
          generation, this.pollGeneration, this.destroyedFlag
        )) this.loadInformation(false);
      }, 5000);
    },
    stopPolling() {
      if (this.pollTimer) window.clearTimeout(this.pollTimer);
      this.pollTimer = null;
    },
    returnToModify() {
      this.pollGeneration += 1;
      this.stopPolling();
      this.step = 0;
    },
    async next() {
      if (this.step === 0) await this.saveApplication();
      else if (this.step === 1) await this.confirmApplication();
    },
    async saveApplication() {
      try {
        const payload = this.$refs.applicationForm.getPayload(this.orderNo);
        this.submitting = true;
        this.ensureMutationSuccess(await saveInformation(this.channelCode, payload));
        await this.loadInformation(false);
        if (this.step === 0) this.step = 1;
      } catch (error) {
        this.$notification.error({ message: this.errorText(error, '投保信息保存失败') });
      } finally {
        this.submitting = false;
      }
    },
    async confirmApplication() {
      try {
        this.$refs.noticeStep.validate();
        this.submitting = true;
        this.ensureMutationSuccess(await confirmInsure(this.channelCode, {
          orderNo: this.orderNo,
        }));
        await this.loadInformation(false);
        if (this.step === 1) this.step = 2;
      } catch (error) {
        this.$notification.error({ message: this.errorText(error, '确认投保失败') });
      } finally {
        this.submitting = false;
      }
    },
    confirmGiveUp() {
      this.$confirm({
        title: '请确认是否取消投保',
        okText: '确认取消',
        cancelText: '暂不取消',
        onOk: async () => {
          this.submitting = true;
          try {
            this.ensureMutationSuccess(await giveUp(this.channelCode, {
              orderNo: this.orderNo,
            }));
            this.$notification.success({ message: '取消投保成功' });
            await this.loadInformation(false);
          } catch (error) {
            this.$notification.error({ message: this.errorText(error, '取消投保失败') });
          } finally {
            this.submitting = false;
          }
        },
      });
    },
    async submitSurrender(values) {
      this.submitting = true;
      try {
        this.ensureMutationSuccess(await surrender(this.channelCode, {
          orderNo: this.orderNo,
          responsiblePersonPhone: this.orderInfo.responsiblePersonPhone || this.orderInfo.mobile,
          ...values,
        }));
        this.$notification.success({ message: '退保申请已提交' });
        await this.loadInformation(false);
      } catch (error) {
        this.$notification.error({ message: this.errorText(error, '退保申请失败') });
      } finally {
        this.submitting = false;
      }
    },
    async submitInvoice(values) {
      this.submitting = true;
      try {
        this.ensureMutationSuccess(await applyInvoice(this.channelCode, {
          orderNo: this.orderNo,
          responsiblePersonPhone: this.orderInfo.responsiblePersonPhone || this.orderInfo.mobile,
          ...values,
        }));
        this.$notification.success({ message: '开票申请已提交' });
        await this.loadInformation(false);
      } catch (error) {
        this.$notification.error({ message: this.errorText(error, '开票申请失败') });
      } finally {
        this.submitting = false;
      }
    },
    openUrl(url) {
      if (!url) {
        this.$notification.warning({ message: '链接尚未生成' });
        return;
      }
      try {
        const target = new URL(url, window.location.origin);
        if (!/^https?:$/.test(target.protocol)) throw new Error('链接协议不受支持');
        window.open(target.href, '_blank', 'noopener,noreferrer');
      } catch (error) {
        this.$notification.error({ message: error.message || '链接格式错误' });
      }
    },
  },
};
</script>

<style lang="less" scoped>
.quota-insure-page {
  min-height: 726px;
  margin-top: 30px;
  background: #fff;
}
.main { padding: 30px 50px 90px; }
.error-block { padding: 40px 50px; }
.operate {
  margin-top: 46px;
  text-align: center;
  button { min-width: 180px; height: 46px; margin: 0 14px; }
}
</style>
